import { useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import {
    ResponsiveContainer,
    ComposedChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';
import { DefaultTooltipContent } from 'recharts/lib/component/DefaultTooltipContent';

import apiBaseUrl from '../config/api';

function formatPeriodLabel(viewType, startTimestamp, endTimestamp, offset) {
    if (!startTimestamp || !endTimestamp) return '';

    const startDate = new Date(startTimestamp * 1000);
    const endDate = new Date(endTimestamp * 1000);

    if (viewType === 'day') {
        if (offset === 0) return 'Vandaag';

        return startDate.toLocaleDateString('nl-NL', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    }

    const startLabel = startDate.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'long'
    });
    const endLabel = endDate.toLocaleDateString('nl-NL', {
        day: 'numeric',
        month: 'long'
    });

    return `${startLabel} t/m ${endLabel}`;
}

function formatTimestampLabel(timestamp, viewType) {
    const date = new Date(timestamp * 1000);

    if (viewType === 'day') {
        return date.toLocaleTimeString('nl-NL', {
            hour: '2-digit',
            minute: '2-digit'
        });
    }

    return date.toLocaleString('nl-NL', {
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit'
    });
}

function formatHeaterOnMinutes(totalMinutes) {
    if (typeof totalMinutes !== 'number' || Number.isNaN(totalMinutes)) return '';

    const safeMinutes = Math.max(0, Math.round(totalMinutes));
    const hours = Math.floor(safeMinutes / 60);
    const minutes = safeMinutes % 60;
    const parts = [];

    if (hours > 0) {
        parts.push(hours === 1 ? '1 uur' : `${hours} uur`);
    }

    if (minutes > 0 || parts.length === 0) {
        parts.push(minutes === 1 ? '1 minuut' : `${minutes} minuten`);
    }

    if (parts.length === 2) return `${parts[0]} en ${parts[1]}`;

    return parts[0] || '';
}

function HistoryModal({ show, setShow, getUserToken }) {
    const navigate = useNavigate();

    const [viewType, setViewType] = useState('day');
    const [offset, setOffset] = useState(0);
    const [chartData, setChartData] = useState([]);
    const [heaterOnMinutes, setHeaterOnMinutes] = useState(null);
    const [periodLabel, setPeriodLabel] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    const [rangeTimestamps, setRangeTimestamps] = useState({ start: null, end: null });
    const [shouldAnimate, setShouldAnimate] = useState(true);

    const fetchControllerRef = useRef(null);
    const fetchIntervalRef = useRef(null);

    const canGoNext = useMemo(() => offset > 0, [offset]);
    const previousLabel = viewType === 'day' ? 'Vorige dag' : 'Vorige week';
    const nextLabel = viewType === 'day' ? 'Volgende dag' : 'Volgende week';

    useEffect(() => {
        if (show) return;

        if (fetchControllerRef.current) {
            fetchControllerRef.current.abort();
            fetchControllerRef.current = null;
        }

        if (fetchIntervalRef.current) {
            clearInterval(fetchIntervalRef.current);
            fetchIntervalRef.current = null;
        }

        setViewType('day');
        setOffset(0);
        setChartData([]);
        setPeriodLabel('');
        setError(null);
        setRangeTimestamps({ start: null, end: null });
        setShouldAnimate(true);
        setHeaterOnMinutes(null);
    }, [show]);

    useEffect(() => {
        if (!show) return undefined;

        const performFetch = (withLoader) => {
            const token = getUserToken ? getUserToken() : localStorage.getItem('user_token');

            if (!token) {
                localStorage.removeItem('user_token');
                setShow(false);
                navigate('/login');
                return;
            }

            if (fetchControllerRef.current) fetchControllerRef.current.abort();

            const controller = new AbortController();
            fetchControllerRef.current = controller;

            if (withLoader) {
                setIsLoading(true);
                setError(null);
                setHeaterOnMinutes(null);
            }

            const params = new URLSearchParams();
            params.append('view-type', viewType);
            params.append('offset', offset);

            fetch(`${apiBaseUrl}/thermostat-measurements?${params.toString()}`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` },
                signal: controller.signal
            })
                .then(async response => {
                    if (response.status === 401) {
                        localStorage.removeItem('user_token');
                        setShow(false);
                        navigate('/login');
                        return null;
                    }

                    if (response.status !== 200) {
                        throw new Error('Het ophalen van de meetgegevens is mislukt.');
                    }

                    return response.json();
                })
                .then(data => {
                    if (data === null || controller.signal.aborted) return;

                    const measurements = Array.isArray(data.measurements) ? data.measurements : [];
                    const mappedData = measurements.map(measurement => {
                        const humidityValue = typeof measurement.humidity === 'number' ? measurement.humidity : null;
                        const setTemperature = typeof measurement.setTemperature === 'number' ? measurement.setTemperature : null;
                        const heaterStatus = typeof measurement.heaterStatus === 'number' ? measurement.heaterStatus : null;
                        const heaterBarHeight = (heaterStatus === 1 && typeof setTemperature === 'number') ? setTemperature : null;

                        return {
                            timestamp: measurement.timestamp,
                            label: formatTimestampLabel(measurement.timestamp, viewType),
                            roomTemperature: measurement.roomTemperature,
                            setTemperature,
                            heaterStatus,
                            heaterBarHeight,
                            humidity: humidityValue
                        };
                    });

                    setChartData(mappedData);
                    setRangeTimestamps({ start: data.startTimestamp, end: data.endTimestamp });
                    setPeriodLabel(formatPeriodLabel(viewType, data.startTimestamp, data.endTimestamp, offset));
                    setHeaterOnMinutes(typeof data.heaterOnMinutes === 'number' ? data.heaterOnMinutes : null);
                    setError(null);
                })
                .catch(fetchError => {
                    if (fetchError.name === 'AbortError') return;
                    console.error(fetchError);
                    setError(fetchError.message || 'Het ophalen van de meetgegevens is mislukt.');
                    setHeaterOnMinutes(null);
                })
                .finally(() => {
                    if (withLoader && !controller.signal.aborted) setIsLoading(false);
                    if (fetchControllerRef.current === controller) fetchControllerRef.current = null;
                });
        };

        setShouldAnimate(true);
        performFetch(true);
        fetchIntervalRef.current = setInterval(() => performFetch(false), 60000);

        return () => {
            if (fetchControllerRef.current) {
                fetchControllerRef.current.abort();
                fetchControllerRef.current = null;
            }

            if (fetchIntervalRef.current) {
                clearInterval(fetchIntervalRef.current);
                fetchIntervalRef.current = null;
            }
        };
    }, [show, viewType, offset, getUserToken, navigate, setShow]);

    useEffect(() => {
        if (!show) return;
        if (!shouldAnimate) return;
        if (chartData.length === 0) return;

        const frame = requestAnimationFrame(() => setShouldAnimate(false));
        return () => cancelAnimationFrame(frame);
    }, [chartData, show, shouldAnimate]);

    function close() {
        setShow(false);
    }

    function handleViewChange(type) {
        if (type === viewType) return;

        setViewType(type);
        setOffset(0);
    }

    function goPrevious() {
        setOffset(currentOffset => currentOffset + 1);
    }

    function goNext() {
        if (!canGoNext) return;
        setOffset(currentOffset => Math.max(0, currentOffset - 1));
    }

    function renderMessage(text, modifier) {
        const className = modifier ? `history-modal__message history-modal__message--${modifier}` : 'history-modal__message';
        return <div className={className}>{text}</div>;
    }

    function renderTooltipContent(tooltipProps) {
        const { active, payload, label } = tooltipProps;

        if (!active || !Array.isArray(payload) || payload.length === 0) return null;

        const filteredPayload = [];
        const seenKeys = new Set();

        for (let index = payload.length - 1; index >= 0; index -= 1) {
            const item = payload[index];
            const key = item && typeof item.dataKey === 'string' ? item.dataKey : undefined;

            if (key && seenKeys.has(key)) continue;

            if (key) seenKeys.add(key);

            if (key === 'heaterBarHeight' && item) {
                item.color = 'rgba(255, 107, 107, 1)';
            }

            filteredPayload.unshift(item);
        }

        if (filteredPayload.length === 0 && (!label || label.trim() === '')) return null;

        const labelEntry = typeof label === 'string' && label.trim() !== ''
            ? [{
                dataKey: '__label',
                value: label,
                name: 'Tijdstip',
                color: '#666666',
                formatter: (value) => [value, 'Tijdstip']
            }]
            : [];

        return <DefaultTooltipContent {...tooltipProps} label={null} payload={[...labelEntry, ...filteredPayload]} />;
    }

    const temperatureDomain = useMemo(() => {
        const temperatures = chartData.reduce((values, entry) => {
            if (typeof entry.roomTemperature === 'number') values.push(entry.roomTemperature);
            if (typeof entry.setTemperature === 'number') values.push(entry.setTemperature);
            return values;
        }, []);

        if (temperatures.length === 0) return ['auto', 'auto'];

        const minValue = Math.min(...temperatures);
        const maxValue = Math.max(...temperatures);
        const range = maxValue - minValue;
        const padding = Math.max(0.5, range * 0.1);

        return [Math.floor((minValue - padding) * 10) / 10, Math.ceil((maxValue + padding) * 10) / 10];
    }, [chartData]);

    function renderChart() {
        if (isLoading) {
            return (
                <div className="history-modal__loading">
                    <span className="history-modal__spinner"></span>
                </div>
            );
        }

        if (error) return renderMessage(error, 'error');
        if (chartData.length === 0) return renderMessage('Geen data gevonden', 'empty');

        return (
            <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="label" minTickGap={16} />
                    <YAxis
                        yAxisId="temperature"
                        domain={temperatureDomain}
                        label={{ value: 'Temperatuur (°C)', angle: -90, position: 'insideLeft' }}
                        allowDecimals
                    />
                    <YAxis
                        yAxisId="humidity"
                        orientation="right"
                        domain={[0, 100]}
                        ticks={[0, 20, 40, 60, 80, 100]}
                        tickFormatter={(value) => `${value}%`}
                        label={{ value: 'Luchtvochtigheid (%)', angle: 90, position: 'insideRight', offset: 10 }}
                    />
                    <Tooltip
                        content={renderTooltipContent}
                        formatter={(value, name, entry) => {
                            if (entry && entry.dataKey === '__label') return [value, 'Tijdstip'];

                            if (entry && entry.dataKey === 'roomTemperature') {
                                if (typeof value === 'number') return [`${value.toFixed(1)} °C`, 'Temperatuur'];
                                return [value, 'Temperatuur'];
                            }

                            if (entry && entry.dataKey === 'setTemperature') {
                                if (typeof value === 'number') return [`${value.toFixed(1)} °C`, 'Ingestelde temperatuur'];
                                return [value, 'Ingestelde temperatuur'];
                            }

                            if (entry && entry.dataKey === 'humidity') {
                                if (typeof value === 'number') return [`${value}%`, 'Luchtvochtigheid'];
                                return [value, 'Luchtvochtigheid'];
                            }

                            if (entry && entry.dataKey === 'heaterBarHeight') {
                                const heaterStatus = entry.payload && typeof entry.payload.heaterStatus === 'number' ? entry.payload.heaterStatus : null;
                                if (heaterStatus === 1) return ['Aan', 'Verwarming'];
                                if (heaterStatus === 0) return ['Uit', 'Verwarming'];
                                return [value !== null && value !== undefined ? 'Aan' : 'Uit', 'Verwarming'];
                            }

                            return null;
                        }}
                    />
                    <Legend
                        payload={[
                            { value: 'Temperatuur', type: 'line', id: 'roomTemperature', color: '#ff7f50' },
                            { value: 'Ingestelde temperatuur', type: 'line', id: 'setTemperature', color: '#3a7bd5' },
                            { value: 'Verwarming', type: 'rect', id: 'heaterStatus', color: 'rgba(255, 107, 107, 1)' },
                            { value: 'Luchtvochtigheid', type: 'line', id: 'humidity', color: '#32a852' }
                        ]}
                    />
                    <Line
                        yAxisId="temperature"
                        type="monotone"
                        dataKey="roomTemperature"
                        stroke="#ff7f50"
                        strokeWidth={2}
                        dot={false}
                        name="Temperatuur"
                        isAnimationActive={shouldAnimate}
                    />
                    <Line
                        yAxisId="temperature"
                        type="stepAfter"
                        dataKey="setTemperature"
                        stroke="#3a7bd5"
                        strokeWidth={2}
                        dot={false}
                        name="Ingestelde temperatuur"
                        isAnimationActive={shouldAnimate}
                    />
                    <Bar
                        yAxisId="temperature"
                        dataKey="heaterBarHeight"
                        fill="rgba(255, 107, 107, 0.3)"
                        name="Verwarming"
                        isAnimationActive={shouldAnimate}
                    />
                    <Line
                        yAxisId="humidity"
                        type="monotone"
                        dataKey="humidity"
                        stroke="#32a852"
                        strokeWidth={2}
                        dot={false}
                        name="Luchtvochtigheid"
                        connectNulls
                        isAnimationActive={shouldAnimate}
                    />
                </ComposedChart>
            </ResponsiveContainer>
        );
    }

    const startDate = rangeTimestamps.start ? new Date(rangeTimestamps.start * 1000) : null;
    const endDate = rangeTimestamps.end ? new Date(rangeTimestamps.end * 1000) : null;
    const startDateLabel = startDate ? startDate.toLocaleDateString('nl-NL') : null;
    const endDateLabel = endDate ? endDate.toLocaleDateString('nl-NL') : null;
    const periodRangeLabel = (startDateLabel && endDateLabel)
        ? (startDateLabel === endDateLabel ? startDateLabel : `${startDateLabel} – ${endDateLabel}`)
        : null;

    const formattedHeaterOnMinutes = typeof heaterOnMinutes === 'number'
        ? formatHeaterOnMinutes(heaterOnMinutes)
        : '';

    return (
        <div className={'history-modal' + (show ? ' history-modal--open' : '')} aria-hidden={!show}>
            <div className="history-modal__overlay" onClick={close}></div>

            <div className="history-modal__panel" role="dialog" aria-modal="true">
                <div className="history-modal__header">
                    <div className="history-modal__view-switch">
                        <label className={'history-modal__view-option' + (viewType === 'day' ? ' history-modal__view-option--active' : '')}>
                            <input type="radio" name="history-view" value="day" checked={viewType === 'day'} onChange={() => handleViewChange('day')} />
                            <span>Dag</span>
                        </label>

                        <label className={'history-modal__view-option' + (viewType === 'week' ? ' history-modal__view-option--active' : '')}>
                            <input type="radio" name="history-view" value="week" checked={viewType === 'week'} onChange={() => handleViewChange('week')} />
                            <span>Week</span>
                        </label>
                    </div>

                    <button type="button" className="history-modal__close-btn" onClick={close}>Sluiten</button>
                </div>

                <div className="history-modal__period-group">
                    <div className="history-modal__period">
                        <span className="history-modal__period-label">{periodLabel}</span>
                        {periodRangeLabel &&
                            <span className="history-modal__period-range">{periodRangeLabel}</span>
                        }
                    </div>

                    <div className="history-modal__nav">
                        <button type="button" className="history-modal__nav-btn history-modal__nav-btn--previous" onClick={goPrevious} disabled={isLoading}>
                            {previousLabel}
                        </button>

                        <button type="button" className="history-modal__nav-btn history-modal__nav-btn--next" onClick={goNext} disabled={!canGoNext || isLoading}>
                            {nextLabel}
                        </button>
                    </div>
                </div>

                <div className="history-modal__chart">
                    {renderChart()}
                </div>

                {formattedHeaterOnMinutes && !isLoading && !error && chartData.length > 0 &&
                    <div className="history-modal__heater-on">
                        Verwarming aan: {formattedHeaterOnMinutes}
                    </div>
                }
            </div>
        </div>
    );
}

export default HistoryModal;

